import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  Popconfirm,
  Switch,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "./Services/Api";           // adjust if your path differs
import { baseURL } from "./api";
import ProfilePricingModal from "./ProfilePricing"; // ⬅️ new import

const ViewMenu = () => {
  /* ────────────────────────────────────────── state */
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [addOns, setAddOns] = useState([]);

  /* ↳ profile‑pricing */
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  /* ────────────────────────────────────────── form */
  const [form] = Form.useForm();

  /* ────────────────────────────────────────── lifecycle */
  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const vendorId = localStorage.getItem("vendorId");
      const role = localStorage.getItem("role");

      let { data: vendorRestaurants } = await axios.get(
        `/getRestaurant?vendorId=${vendorId}`
      );

      let adminRestaurants = [];
      if (role === "admin") {
        const { data } = await axios.get(`/getRestaurant`);
        adminRestaurants = data.filter((r) => r.default === "true");
      }

      const merged = [...vendorRestaurants, ...adminRestaurants].filter(
        (r, idx, self) => idx === self.findIndex((x) => x._id === r._id)
      );
      setRestaurants(merged);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch restaurants.");
    }
  };

  const fetchMenuItems = async (restaurantId) => {
    try {
      const { data } = await axios.get(
        `/getMenuItems?restaurantId=${restaurantId}`
      );
      setMenuItems(data);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch menu items.");
    }
  };

  /* ────────────────────────────────────────── edit flow */
 const handleEdit = (item) => {
  setEditingItem(item);

  if (item.customUnits?.length) {
    // custom unit pricing
    form.setFieldsValue({
      name: item.name,
      description: item.description,
      active: item.active,
    });
  } else {
    // normal pricing
    form.setFieldsValue({
      name: item.name,
      price1kg: item.price1kg,
      price500gm: item.price500gm,
      description: item.description,
      active: item.active,
    });
  }

  setAddOns(item.addOns || []);
  setFileList(
    item.imgPath
      ? [{
          uid: "-1",
          name: "image.png",
          status: "done",
          url: `${baseURL}/${item.imgPath}`,
        }]
      : []
  );
};


 const handleSubmitEdit = async (values) => {
  const fd = new FormData();

  fd.append("name", values.name);
  fd.append("description", values.description);
  fd.append("active", values.active);
  fd.append("addOns", JSON.stringify(addOns));

  if (hasCustomUnits) {
    fd.append("customUnits", JSON.stringify(editingItem.customUnits));
  } else {
    if (values.price500gm != null) fd.append("price500gm", values.price500gm);
    fd.append("price1kg", values.price1kg);
  }

  if (fileList[0]?.originFileObj) {
    fd.append("img", fileList[0].originFileObj);
  }

  await axios.put(`/updateMenuItem/${editingItem._id}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  message.success("Menu item updated");
};

  const handleDeleteMenuItem = async (id) => {
    try {
      await axios.delete(`/deleteMenuItem/${id}`);
      message.success("Menu item deleted");
      setMenuItems((p) => p.filter((i) => i._id !== id));
    } catch (err) {
      console.error(err);
      message.error("Delete failed");
    }
  };
const hasCustomUnits =
  editingItem && Array.isArray(editingItem.customUnits) && editingItem.customUnits.length > 0;

  /* ────────────────────────────────────────── render */
  return (
    <>
      <h2>View Menu</h2>

      {/* ─────  CHOOSE A RESTAURANT  ───── */}
      {!selectedRestaurant ? (
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {restaurants.map((r) => (
            <Card
              key={r._id}
              title={r.name}
              bordered
              style={{ width: 300, cursor: "pointer" }}
              onClick={() => {
                setSelectedRestaurant(r);
                fetchMenuItems(r._id);
              }}
            >
              <img
                src={`${baseURL}/${r.imgPath}`}
                alt={r.name}
                style={{ width: "30%", height: 75, objectFit: "cover" }}
              />
              <p>Click to view menu</p>
            </Card>
          ))}
        </div>
      ) : (
        /* ─────  MENU LIST  ───── */
        <>
          <h3>{selectedRestaurant.name} ‑ Menu</h3>

          <img
            src={`${baseURL}/${selectedRestaurant.imgPath}`}
            alt={selectedRestaurant.name}
            style={{ width: "14%", height: 155, objectFit: "cover" }}
          />

          <Button
            type="primary"
            style={{  }}
            onClick={() => setProfileModalOpen(true)}  // open modal
          >
            Profile Pricing
          </Button>

          {menuItems.length ? (
            <Table
  dataSource={menuItems.map((m, i) => ({ ...m, key: i }))}
  pagination={false}
  columns={[
    {
      title: "Image",
      dataIndex: "imgPath",
      render: (p) => (
        <img
          src={`${baseURL}/${p}`}
          alt="item"
          style={{ width: 50, height: 50, objectFit: "cover" }}
        />
      ),
    },
    { title: "Item", dataIndex: "name" },

    {
  title: "Price 500gm",
  render: (_, record) =>
    record.customUnits?.length > 0 ? (
      <span style={{ color: "#999" }}>—</span>
    ) : record.price500gm > 0 ? (
      `₹${record.price500gm}`
    ) : (
      <span style={{ color: "#999" }}>—</span>
    ),
},

{
  title: "Price 1kg",
  render: (_, record) =>
    record.customUnits?.length > 0 ? (
      <span style={{ color: "#999" }}>—</span>
    ) : record.price1kg > 0 ? (
      `₹${record.price1kg}`
    ) : (
      <span style={{ color: "#999" }}>—</span>
    ),
},

{
  title: "Custom Price",
  render: (_, record) =>
    record.customUnits?.length > 0 ? (
      <span style={{  color: "#000000ff" }}>
        ₹{record.customUnits[0].price}
      </span>
    ) : (
      <span style={{ color: "#999" }}>—</span>
    ),
},


    { title: "Description", dataIndex: "description" },

    {
      title: "Status",
      dataIndex: "active",
      render: (a) => (
        <span style={{ color: a ? "green" : "red" }}>
          {a ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      title: "Action",
      render: (_, rec) => (
        <div style={{ display: "inline-flex", gap: 10 }}>
          <Button onClick={() => handleEdit(rec)}>Edit</Button>
          <Popconfirm
            title="Delete this item?"
            onConfirm={() => handleDeleteMenuItem(rec._id)}
          />
        </div>
      ),
    },
  ]}
/>

          ) : (
            <p>No menu available.</p>
          )}

          <Button
            style={{ marginTop: 20 }}
            onClick={() => setSelectedRestaurant(null)}
          >
            Back to Home
          </Button>
        </>
      )}

      {/* ────────────────────────────────────────── EDIT ITEM MODAL */}
      <Modal
        title="Edit Menu Item"
        open={!!editingItem}
        okText="Update"
        onCancel={() => {
          setEditingItem(null);
          form.resetFields();
          setFileList([]);
          setAddOns([]);
        }}
        onOk={() => form.validateFields().then(handleSubmitEdit)}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="name"
            label="Item Name"
            rules={[{ required: true, message: "Enter name" }]}
          >
            <Input />
          </Form.Item>
{/* ───── PRICING SECTION ───── */}
{hasCustomUnits ? (
  <>
    <h4>Custom Unit Pricing</h4>

    {editingItem.customUnits.map((cu, idx) => (
      <Form.Item
        key={cu._id}
        label={`${cu.qty} ${cu.unit}`}
      >
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          defaultValue={cu.price}
          onChange={(val) => {
            setEditingItem((prev) => {
              const updated = [...prev.customUnits];
              updated[idx] = { ...updated[idx], price: val };
              return { ...prev, customUnits: updated };
            });
          }}
        />
      </Form.Item>
    ))}
  </>
) : (
  <>
    <Form.Item
      name="price500gm"
      label="Price (500 gm)"
    >
      <InputNumber style={{ width: "100%" }} min={0} />
    </Form.Item>

    <Form.Item
      name="price1kg"
      label="Price (1 Kg)"
      rules={[{ required: true }]}
    >
      <InputNumber style={{ width: "100%" }} min={0} />
    </Form.Item>
  </>
)}


          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="active" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item label="Add‑ons">
            {addOns.map((a, idx) => (
              <div
                key={idx}
                style={{ marginBottom: 8, display: "flex", gap: 8 }}
              >
                <Input
                  value={a}
                  onChange={(e) =>
                    setAddOns((p) => {
                      const n = [...p];
                      n[idx] = e.target.value;
                      return n;
                    })
                  }
                  style={{ flex: 1 }}
                />
                <Button
                  danger
                  onClick={() =>
                    setAddOns((p) => p.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={() => setAddOns((p) => [...p, ""])}>
              Add Add‑on
            </Button>
          </Form.Item>

          <Form.Item label="Image">
            <Upload
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFileList(fileList)}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* ────────────────────────────────────────── PROFILE PRICING MODAL */}
      {selectedRestaurant && (
        <ProfilePricingModal
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          restaurantId={selectedRestaurant._id}
          menuItems={menuItems}
        />
      )}
    </>
  );
};

export default ViewMenu;
